import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { createPath, useBlocker } from 'react-router-dom';
import { ONE_HOUR } from 'shared/constants/NumberConstants';
import { trackEvent } from 'shared/helpers/Analytics';
import { useVisibilityState } from 'shared/helpers/BrowserHelpers';
import { useBasename } from 'shared/helpers/RoutingHelpers';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import { requestBuildInfo } from './data/AppVersionApi';

const APP_NAME = import.meta.env.VITE_APP_NAME.toUpperCase();

function useForceRefreshOnNavigation(
  shouldForceRefresh: boolean,
  buildInfo?: AppBuildInfo,
) {
  const basename = useBasename();
  useBlocker(({ nextLocation }) => {
    if (shouldForceRefresh) {
      trackEvent(
        'Force Refreshed On Navigation',
        { app_name: APP_NAME, ...buildInfo },
        () => {
          openExternalURL(basename + createPath(nextLocation));
        },
      );
    }

    return false;
  });
}

interface AppBuildInfo {
  created_at: string;
}

function useAppVersionChecker() {
  const [currentBuildInfo, setCurrentBuildInfo] = useState<AppBuildInfo>();
  const [shouldForceRefresh, setShouldForceRefresh] = useState(false);
  const visibilityStatus = useVisibilityState();

  const { data } = useQuery<AppBuildInfo>({
    queryKey: ['version'],
    queryFn: requestBuildInfo,
    refetchInterval: visibilityStatus === 'visible' ? ONE_HOUR : false,
  });

  useEffect(() => {
    if (currentBuildInfo && import.meta.env.MODE !== 'development' && data) {
      setShouldForceRefresh(currentBuildInfo.created_at !== data.created_at);
    } else {
      setCurrentBuildInfo(data);
    }
  }, [data, currentBuildInfo, setCurrentBuildInfo]);

  useForceRefreshOnNavigation(shouldForceRefresh, currentBuildInfo);
}

export function AppVersionChecker() {
  useAppVersionChecker();

  return null;
}
